import React from 'react';
import { TABLE_CONSTANTS } from '@saga/shared';

interface TableResizeHandleProps {
    onResize: (columnIndex: number, newWidth: number) => void;
    columnIndex: number;
    isActive: boolean;
    initialWidth: number;
    setHoveringColumn: (column: number | null) => void;
    onPreviewChange: (columnIndex: number | null, width: number | null) => void;
}

export function TableResizeHandle({
    onResize,
    columnIndex,
    initialWidth,
    setHoveringColumn,
    onPreviewChange,
}: TableResizeHandleProps) {
    const [isDragging, setIsDragging] = React.useState(false);
    const startX = React.useRef<number>(0);
    const startWidth = React.useRef<number>(0);

    const handleMouseDown = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
            startX.current = e.clientX;
            startWidth.current = initialWidth;
        },
        [initialWidth],
    );

    React.useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const delta = e.clientX - startX.current;
            const newWidth = Math.max(TABLE_CONSTANTS.MIN_COLUMN_WIDTH, startWidth.current + delta);
            // Update preview at table level
            onPreviewChange(columnIndex, newWidth);
        };

        const handleMouseUp = (e: MouseEvent) => {
            const delta = e.clientX - startX.current;
            const finalWidth = Math.max(TABLE_CONSTANTS.MIN_COLUMN_WIDTH, startWidth.current + delta);
            // Update Slate model only on mouseUp
            onResize(columnIndex, finalWidth);

            setIsDragging(false);
            onPreviewChange(null, null);
            setHoveringColumn(null);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, columnIndex, onResize, setHoveringColumn, onPreviewChange]);

    return (
        <div
            onMouseDown={handleMouseDown}
            onMouseEnter={() => setHoveringColumn(columnIndex)}
            onMouseLeave={() => !isDragging && setHoveringColumn(null)}
            contentEditable={false}
            className="absolute top-0 bottom-0 w-2 cursor-col-resize z-20 select-none"
            style={{
                right: -4,
                touchAction: 'none',
            }}
        />
    );
}
